import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Github, ExternalLink, ChevronLeft, ChevronRight, Monitor, Mail, MessageSquare, Video, Users, FolderOpen, FileText, Table, BookOpen, Rss, LayoutGrid, PenTool, Terminal, Code2, Globe, Calculator, Clock, CreditCard as Edit3, Camera, Clipboard, Volume2, Lock, Film, Image, Settings, ShoppingBag, Activity, Trash2, CircleUser as UserCircle, Shield, Search, Package, Layers, Zap, Database, Star, ArrowRight } from 'lucide-react';
import AICapabilities from '../components/ai/AICapabilities';

const YOUTUBE_ID = '3GVGymIFnX4';

const stats = [
  { value: '30+', label: 'Applications' },
  { value: '5', label: 'Languages' },
  { value: '15', label: 'Desktop Widgets' },
  { value: '35+', label: 'E2E Tests' },
  { value: '20+', label: 'Docker Services' },
];

const features = [
  {
    icon: Monitor,
    title: 'Window Manager',
    description: 'Drag, resize, snap, minimize, maximize, Alt+Tab, Picture-in-Picture — a real desktop experience in the browser.',
  },
  {
    icon: Shield,
    title: 'Single Sign-On',
    description: 'Keycloak OIDC with PKCE, role-based access control: user, developer, admin — enterprise-grade auth out of the box.',
  },
  {
    icon: Globe,
    title: '5 Languages',
    description: 'French, English, Italian, Spanish, German. 560+ translation keys per language, switchable at runtime.',
  },
  {
    icon: Layers,
    title: 'Themes & Widgets',
    description: '7 themes (Midnight, Ocean, Aurora, Forest, Sunset, Light, Rose), 15 desktop widgets, custom wallpapers and accent colors.',
  },
  {
    icon: Search,
    title: 'Spotlight Search',
    description: 'Press Ctrl+K to instantly find and launch any app, file or setting from anywhere in the OS.',
  },
  {
    icon: Package,
    title: 'Docker Compose',
    description: 'Full stack in one command. Traefik v3 reverse proxy, TLS termination, 20+ services wired together and ready to go.',
  },
];

const appCategories = [
  {
    name: 'Communication',
    color: 'from-blue-500/20 to-cyan-500/20',
    border: 'border-blue-500/30',
    apps: [
      { icon: Mail, name: 'Email', sub: 'Zimbra Webmail' },
      { icon: Monitor, name: 'Calendar', sub: 'Zimbra Calendar' },
      { icon: MessageSquare, name: 'Chat', sub: 'Matrix / Element' },
      { icon: Video, name: 'Video', sub: 'LiveKit WebRTC' },
      { icon: Users, name: 'Directory', sub: 'Employee directory' },
    ],
  },
  {
    name: 'Productivity',
    color: 'from-emerald-500/20 to-teal-500/20',
    border: 'border-emerald-500/30',
    apps: [
      { icon: FolderOpen, name: 'Files', sub: 'Nextcloud WebDAV' },
      { icon: FileText, name: 'Office', sub: 'OnlyOffice' },
      { icon: Table, name: 'Spreadsheets', sub: 'Grist' },
      { icon: BookOpen, name: 'Wiki', sub: 'Outline' },
      { icon: Rss, name: 'Blog', sub: 'Ghost' },
      { icon: LayoutGrid, name: 'Kanban', sub: 'Native drag & drop' },
      { icon: PenTool, name: 'Whiteboard', sub: 'Excalidraw' },
    ],
  },
  {
    name: 'Tools',
    color: 'from-amber-500/20 to-orange-500/20',
    border: 'border-amber-500/30',
    apps: [
      { icon: Terminal, name: 'Terminal', sub: 'Xpra Linux' },
      { icon: Code2, name: 'Code', sub: 'VS Code server' },
      { icon: Globe, name: 'Browser', sub: 'Chromium' },
      { icon: Calculator, name: 'Calculator', sub: 'Scientific' },
      { icon: Clock, name: 'World Clock', sub: 'Timezones' },
      { icon: Edit3, name: 'Text Editor', sub: 'Monaco engine' },
      { icon: Camera, name: 'Screenshot', sub: 'Capture & record' },
      { icon: Clipboard, name: 'Clipboard', sub: 'History manager' },
      { icon: Volume2, name: 'Audio', sub: 'Volume mixer' },
      { icon: Lock, name: 'Passwords', sub: 'Vaultwarden' },
    ],
  },
  {
    name: 'Multimedia & System',
    color: 'from-rose-500/20 to-pink-500/20',
    border: 'border-rose-500/30',
    apps: [
      { icon: Film, name: 'Media', sub: 'Jellyfin' },
      { icon: Image, name: 'Photos', sub: 'Immich + AI' },
      { icon: Settings, name: 'Settings', sub: 'Themes, shortcuts' },
      { icon: ShoppingBag, name: 'App Store', sub: 'Enable / disable apps' },
      { icon: Activity, name: 'Task Manager', sub: 'Process manager' },
      { icon: Zap, name: 'Widgets', sub: 'Widget manager' },
      { icon: Trash2, name: 'Trash', sub: 'Deleted files' },
      { icon: UserCircle, name: 'Profile', sub: 'User profile' },
      { icon: Shield, name: 'Admin IAM', sub: 'Keycloak console' },
    ],
  },
];

const screenshots = [
  { src: '/01-desktop.png', label: 'Desktop', caption: 'Clean desktop with app icons, taskbar and virtual workspaces' },
  { src: '/10-multi-window.png', label: 'Kanban', caption: 'Native Kanban boards with drag & drop and multi-column sprints' },
  { src: '/09-text-editor.png', label: 'Text Editor', caption: 'Monaco-powered editor with syntax highlighting and multi-tab support' },
  { src: '/08-calculator.png', label: 'Calculator', caption: 'Scientific calculator with history, fully integrated into the OS' },
  { src: '/07-settings.png', label: 'Settings', caption: '7 beautiful themes — Midnight, Ocean, Aurora, Forest, Sunset, Light, Rose' },
];

const techStack = [
  { name: 'React 18', color: '#61DAFB' },
  { name: 'TypeScript', color: '#3178C6' },
  { name: 'Vite', color: '#646CFF' },
  { name: 'Tailwind v4', color: '#06B6D4' },
  { name: 'Zustand', color: '#FF6B35' },
  { name: 'Docker', color: '#2496ED' },
  { name: 'Traefik v3', color: '#24A1C1' },
  { name: 'Keycloak 24', color: '#4D9B44' },
  { name: 'Nextcloud', color: '#0082C9' },
  { name: 'Matrix/Synapse', color: '#0DBD8B' },
  { name: 'LiveKit', color: '#E03131' },
  { name: 'Playwright', color: '#45BA4B' },
];

export default function ScaleNixOSPage() {
  const [playing, setPlaying] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setCurrentSlide((c) => (c + 1) % screenshots.length);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  const prev = () => setCurrentSlide((c) => (c - 1 + screenshots.length) % screenshots.length);
  const next = () => setCurrentSlide((c) => (c + 1) % screenshots.length);

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white font-sans">

      {/* ── NAV ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0a0e1a]/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <a
              href="/"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/20 text-xs font-medium transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Back
            </a>
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Monitor className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-white text-sm tracking-tight">ScaleNix OS</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://github.com/ScaleNix/ScaleNixOS"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/20 text-xs font-medium transition-colors"
            >
              <Github className="h-3.5 w-3.5" />
              GitHub
            </a>
            <a
              href="https://scalenix.github.io/ScaleNixOS/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Live Demo
            </a>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/25 mb-6">
              <Star className="h-3.5 w-3.5 text-blue-400" />
              <span className="text-blue-300 text-sm font-semibold">Open Source — LGPL-2.0</span>
            </div>

            <h1 className="text-6xl lg:text-7xl font-extrabold mb-4 tracking-tight">
              <span className="bg-gradient-to-r from-white via-blue-100 to-cyan-300 bg-clip-text text-transparent">
                ScaleNix OS
              </span>
            </h1>

            <p className="text-2xl text-blue-200 font-medium mb-4">Your workspace, in one tab.</p>

            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              A full-featured web desktop environment with 30+ integrated apps. Email, files, chat, video, documents — unified behind a single sign-on, running entirely in the browser.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://github.com/ScaleNix/ScaleNixOS"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-gray-900 font-bold rounded-xl hover:bg-gray-100 transition-colors shadow-xl"
              >
                <Github className="h-4 w-4" />
                View on GitHub
              </a>
              <a
                href="https://scalenix.github.io/ScaleNixOS/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl transition-colors shadow-lg shadow-blue-500/30"
              >
                <ExternalLink className="h-4 w-4" />
                Deploy Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── VIDEO ── */}
      <section className="py-8 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative"
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black aspect-video">
              {!playing ? (
                <>
                  <img
                    src={`https://img.youtube.com/vi/${YOUTUBE_ID}/maxresdefault.jpg`}
                    alt="ScaleNix OS Demo"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${YOUTUBE_ID}/hqdefault.jpg`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  <button
                    onClick={() => setPlaying(true)}
                    className="absolute inset-0 flex flex-col items-center justify-center group"
                  >
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-20 h-20 bg-white/95 rounded-full flex items-center justify-center shadow-2xl group-hover:bg-white transition-colors"
                    >
                      <Play className="h-8 w-8 text-blue-600 ml-1" fill="currentColor" />
                    </motion.div>
                    <span className="mt-4 text-white font-semibold text-lg drop-shadow-lg">Watch Demo</span>
                  </button>
                </>
              ) : (
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0`}
                  title="ScaleNix OS Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
            <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/15 to-cyan-500/15 rounded-3xl blur-2xl -z-10" />
          </motion.div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="py-12 border-y border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-6">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="text-center"
              >
                <div className="text-4xl font-extrabold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  {s.value}
                </div>
                <div className="text-sm text-gray-400 mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">Built for the Modern Web</h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Every feature of a real operating system, delivered through a single browser tab.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white/4 border border-white/8 rounded-2xl p-6 hover:bg-white/6 hover:border-blue-500/30 transition-all group"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/20 flex items-center justify-center mb-4 group-hover:border-blue-400/40 transition-colors">
                  <f.icon className="h-5 w-5 text-blue-400" />
                </div>
                <h3 className="text-white font-bold mb-2">{f.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{f.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SCREENSHOT SHOWCASE ── */}
      <section className="py-16 bg-white/2">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-white mb-4">See It In Action</h2>
            <p className="text-gray-400">Real screenshots from the OS running in your browser</p>
          </motion.div>

          <div className="relative">
            <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#0d1120]">
              {/* Window chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-white/4 border-b border-white/8">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
                <div className="flex-1 mx-6 text-xs text-gray-500 font-mono">
                  scalenix.github.io/ScaleNixOS — {screenshots[currentSlide].label}
                </div>
              </div>

              <div className="relative aspect-[16/9] overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentSlide}
                    src={screenshots[currentSlide].src}
                    alt={screenshots[currentSlide].label}
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.35 }}
                    className="w-full h-full object-cover object-top"
                  />
                </AnimatePresence>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-6 py-5">
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={currentSlide}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="text-white text-sm font-medium"
                    >
                      {screenshots[currentSlide].caption}
                    </motion.p>
                  </AnimatePresence>
                </div>
                <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors">
                  <ChevronLeft className="h-4 w-4 text-white" />
                </button>
                <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors">
                  <ChevronRight className="h-4 w-4 text-white" />
                </button>
              </div>
            </div>

            {/* Thumbnails */}
            <div className="flex gap-3 mt-4 justify-center">
              {screenshots.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`relative rounded-lg overflow-hidden border-2 transition-all ${i === currentSlide ? 'border-blue-400 opacity-100' : 'border-white/10 opacity-50 hover:opacity-75'}`}
                  style={{ width: 80, height: 50 }}
                >
                  <img src={s.src} alt={s.label} className="w-full h-full object-cover object-top" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── APP CATALOG ── */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">30+ Integrated Applications</h2>
            <p className="text-gray-400 max-w-xl mx-auto">Every app you need, pre-configured and connected through a unified single sign-on.</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {appCategories.map((cat, ci) => (
              <motion.div
                key={ci}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: ci * 0.1 }}
                className={`bg-gradient-to-br ${cat.color} border ${cat.border} rounded-2xl p-5`}
              >
                <h3 className="text-white font-bold text-sm mb-4 uppercase tracking-wider opacity-80">{cat.name}</h3>
                <ul className="space-y-2.5">
                  {cat.apps.map((app, ai) => (
                    <li key={ai} className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                        <app.icon className="h-3.5 w-3.5 text-white/80" />
                      </div>
                      <div>
                        <div className="text-white text-xs font-semibold leading-tight">{app.name}</div>
                        <div className="text-white/50 text-xs leading-tight">{app.sub}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI CAPABILITIES ── */}
      <AICapabilities accent="from-blue-500 to-cyan-400" productName="ScaleNix OS" />

      {/* ── TECH STACK ── */}
      <section className="py-20 border-y border-white/5 bg-white/2">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white mb-3">Built With</h2>
            <p className="text-gray-400">Modern, battle-tested technology across the entire stack</p>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-3">
            {techStack.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm font-medium text-gray-300 hover:border-white/25 hover:text-white transition-colors"
              >
                <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: t.color }} />
                {t.name}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/15 to-cyan-600/10" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-5">
              Ready to run your OS in a tab?
            </h2>
            <p className="text-xl text-gray-400 mb-10">
              Self-host it in minutes with Docker Compose. Contributions welcome.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://github.com/ScaleNix/ScaleNixOS"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-900 font-bold rounded-xl hover:bg-gray-100 transition-colors text-base shadow-xl"
              >
                <Github className="h-5 w-5" />
                View on GitHub
              </a>
              <a
                href="https://scalenix.github.io/ScaleNixOS/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl transition-colors text-base shadow-lg shadow-blue-500/30"
              >
                Deploy Now
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
                <Monitor className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="font-bold text-white text-sm">ScaleNix OS</div>
                <div className="text-xs text-gray-500">Built by Scalenix — Founded by Malik Benmekki</div>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <a href="https://github.com/ScaleNix/ScaleNixOS" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1.5">
                <Github className="h-4 w-4" /> GitHub
              </a>
              <a href="https://github.com/ScaleNix/ScaleNixOS/blob/main/README.md" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Docs
              </a>
              <a href="https://github.com/ScaleNix/ScaleNixOS/blob/main/CONTRIBUTING.md" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Contributing
              </a>
              <span className="text-gray-600">LGPL-2.0</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
